import React from "react";
import {
  convertCompositeKind,
  reparentNode,
  unwrapComposite,
  wrapNodeInComposite,
} from "../../../behavior/tree";
import MutedText from "../../ui/MutedText";
import MenuSelect from "../../ui/MenuSelect";
import { INPUT_CLASS, Row } from "./NodeInspectorFields";
import { reparentTargetLabel } from "./nodeInspectorTreeHelpers";

export default function NodeStructureControls({
  behaviorDoc,
  currentParentId,
  isRoot,
  node,
  onApplyDocument,
  reparentTargets,
}) {
  return (
    <>
      {behaviorDoc && onApplyDocument && (node.kind === "sequence" || node.kind === "selector") ? (
        <Row label="Type">
          <MenuSelect
            size="compact"
            className={INPUT_CLASS}
            value={node.kind}
            onChange={(k) => {
              if (k !== "sequence" && k !== "selector") return;
              if (k === node.kind) return;
              const next = convertCompositeKind(behaviorDoc, node.id, k);
              if (next) onApplyDocument(next);
            }}
            options={[
              { value: "sequence", label: "Sequence (run all in order)" },
              { value: "selector", label: "Fallback (try in order, first success wins)" },
            ]}
            contentClassName="max-h-[min(240px,50vh)]"
            triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
            aria-label="Composite type"
          />
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              className="flex-1 rounded border border-gray-600 bg-gray-800 px-2 py-1 text-xs text-gray-100 hover:bg-gray-700"
              onClick={() => {
                const next = wrapNodeInComposite(behaviorDoc, node.id, "repeat");
                if (next !== behaviorDoc) onApplyDocument(next);
              }}
            >
              Wrap in Repeat…
            </button>
            <button
              type="button"
              className="flex-1 rounded border border-gray-600 bg-gray-800 px-2 py-1 text-xs text-gray-100 hover:bg-gray-700"
              onClick={() => {
                const next = wrapNodeInComposite(behaviorDoc, node.id, "retry");
                if (next !== behaviorDoc) onApplyDocument(next);
              }}
            >
              Wrap in Retry…
            </button>
          </div>
          <MutedText className="mt-1 text-xs leading-snug">
            Wrapping puts this whole node inside a new Repeat/Retry so it loops or re-attempts as a
            unit.
          </MutedText>
        </Row>
      ) : null}

      {behaviorDoc && onApplyDocument && (node.kind === "retry" || node.kind === "repeat") ? (
        <Row label="Type">
          <MenuSelect
            size="compact"
            className={INPUT_CLASS}
            value={node.kind}
            onChange={(k) => {
              if (k !== "retry" && k !== "repeat") return;
              if (k === node.kind) return;
              const next = convertCompositeKind(behaviorDoc, node.id, k);
              if (next) onApplyDocument(next);
            }}
            options={[
              { value: "repeat", label: "Repeat (loop N times)" },
              { value: "retry", label: "Retry (up to N times until success)" },
            ]}
            contentClassName="max-h-[min(240px,50vh)]"
            triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
            aria-label="Loop type"
          />
          {node.child ? (
            <button
              type="button"
              className="mt-2 w-full rounded border border-gray-600 bg-gray-800 px-2 py-1 text-xs text-gray-100 hover:bg-gray-700"
              onClick={() => {
                const next = unwrapComposite(behaviorDoc, node.id);
                if (next !== behaviorDoc) onApplyDocument(next);
              }}
            >
              Unwrap (remove this {node.kind === "retry" ? "Retry" : "Repeat"})
            </button>
          ) : null}
        </Row>
      ) : null}

      {node.kind === "ifThenElse" ? (
        <MutedText className="text-xs leading-snug">
          This control node has fixed slots in the tree view: Condition, Then, and optional Else.
          Condition accepts condition nodes only.
        </MutedText>
      ) : null}

      {behaviorDoc && onApplyDocument && !isRoot ? (
        <Row label="Nest inside (parent)">
          <MenuSelect
            size="compact"
            className={INPUT_CLASS}
            value={currentParentId || ""}
            onChange={(newParentId) => {
              if (!newParentId || newParentId === currentParentId) return;
              const next = reparentNode(behaviorDoc, node.id, newParentId);
              if (next !== behaviorDoc) onApplyDocument(next);
            }}
            options={[
              ...(currentParentId && !reparentTargets.includes(currentParentId)
                ? [
                    {
                      value: currentParentId,
                      label: `${reparentTargetLabel(behaviorDoc.nodes[currentParentId])} (current)`,
                    },
                  ]
                : []),
              ...reparentTargets.map((id) => ({
                value: id,
                label: `${reparentTargetLabel(behaviorDoc.nodes[id])}${id === currentParentId ? " (current)" : ""}`,
              })),
            ]}
            contentClassName="max-h-[min(320px,50vh)]"
            triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
            aria-label="Parent node"
          />
          <MutedText className="mt-1 text-xs leading-snug">
            Move this node (and everything inside it) under a different composite.
          </MutedText>
        </Row>
      ) : null}
    </>
  );
}
